import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';

import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { EmbalagemService } from '../../../shared/services/apis/produto/embalagem.service';
import { EmbalagemRequest } from '../../../shared/services/requests/produto/embalagem.request';
import { LoadingBoxComponent } from '../../../shared/components/loading-box/loading-box.component';
import { ReturnStruct } from '../../../shared/services/structs/return.struct';
import { ModuloEnum } from '../../../shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '../../../shared/enums/usuario/funcionalidade.enum';
import { GetEmbalagemResponse } from '../../../shared/services/responses/produto/get-embalagem.response';
import { FooterActionComponent } from '../../../shared/components/footer-action/footer-action.component';
import { TipoEmbalagemEnum } from '../../../shared/enums/produto/tipo-embalagem.enum';
import { NgxCurrencyDirective } from 'ngx-currency';
import { MaskService } from '../../../shared/services/mask.service';
import { HeaderComponent } from '../../../shared/components/header/header.component';

@Component({
  selector: 'app-embalagem-cadastro',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RouterModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatSelectModule,
    LoadingBoxComponent,
    FooterActionComponent,
    NgxCurrencyDirective,
    HeaderComponent,
  ],
  templateUrl: './embalagem-cadastro.component.html',
  styleUrl: './embalagem-cadastro.component.css',
})
export class EmbalagemCadastroComponent implements OnInit {
  public TipoEmbalagemEnum = TipoEmbalagemEnum;
  public moduloEnum = ModuloEnum.Cadastros;
  public funcionalidadeEnum = FuncionalidadeEnum.Cadastros_Embalagens;

  public form: FormGroup;
  public isEditMode: boolean = false;
  public isLoading: boolean = false;
  public idEmbalagem: number | null = null;
  public isSaving: boolean = false;
  private isLoadingData: boolean = false;

  public listaTipoEmbalagem = [
    { id: TipoEmbalagemEnum.Envelope, descricao: 'Envelope' },
    { id: TipoEmbalagemEnum.PacoteCaixa, descricao: 'Pacote / Caixa' },
    { id: TipoEmbalagemEnum.RoloCilindro, descricao: 'Rolo / Cilindro' },
  ];

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private embalagemService: EmbalagemService,
    private alertService: AlertService,
    private maskService: MaskService
  ) { }


  public getDecimalMask(precision: number = 2) {
    return { ...this.maskService.getDecimalMask(precision), allowNegative: false }
  }

  ngOnInit(): void {
    this.initForm();
    this.checkIdEmbalagem();
  }

  private initForm(): void {
    this.form = this.fb.group({
      idTipoEmbalagem: [TipoEmbalagemEnum.Envelope, Validators.required],
      descricao: ['', Validators.required],
      larguraCm: [null],
      alturaCm: [null],
      comprimentoCm: [null],
      diametroCm: [null],
      pesoKg: [null],
    });

    this.form.get('idTipoEmbalagem')?.valueChanges.subscribe(val => this.onTipoEmbalagemChange(val));

    // Initialize field states for default type (Envelope)
    this.configureFieldsForTipo(TipoEmbalagemEnum.Envelope);
  }

  private checkIdEmbalagem(): void {
    const paramId = this.route.snapshot.paramMap.get('idEmbalagem');
    if (paramId) {
      this.idEmbalagem = Number(paramId);
      this.isEditMode = true;
      this.loadData(this.idEmbalagem);
    }
  }

  private loadData(id: number): void {
    this.isLoading = true;
    this.isLoadingData = true;
    this.embalagemService.getById(id).subscribe({
      next: (res: GetEmbalagemResponse) => {
        this.isLoading = false;

        if (res.embalagem) {
          console.log('Editando embalagem:', res.embalagem);

          const tipo = Number(res.embalagem.idTipoEmbalagem);
          this.configureFieldsForTipo(tipo);

          this.form.patchValue(
            {
              idTipoEmbalagem: tipo,
              descricao: res.embalagem.descricao,
              larguraCm: res.embalagem.larguraCm ?? null,
              alturaCm: res.embalagem.alturaCm ?? null,
              comprimentoCm: res.embalagem.comprimentoCm ?? null,
              diametroCm: res.embalagem.diametroCm ?? null,
              pesoKg: res.embalagem.pesoKg ?? null,
            },
            { emitEvent: false }
          );
        }
        setTimeout(() => {
          this.isLoadingData = false;
        }, 100);
      },
      error: err => {
        console.error('Erro ao carregar:', err);
        this.isLoading = false;
        this.alertService.show('Erro', 'Não foi possível carregar a embalagem.', AlertType.error);
        this.router.navigate(['/cadastros/embalagens']);
      },
    });
  }

  onTipoEmbalagemChange(tipo: number): void {
    // Skip reset when loading data in edit mode
    if (this.isLoadingData) {
      return;
    }
    // Reset fields to null when user changes type
    this.form.patchValue({
      larguraCm: null,
      alturaCm: null,
      comprimentoCm: null,
      diametroCm: null,
    });
    this.configureFieldsForTipo(tipo);
  }

  private configureFieldsForTipo(tipo: number): void {
    const larguraControl = this.form.get('larguraCm');
    const alturaControl = this.form.get('alturaCm');
    const comprimentoControl = this.form.get('comprimentoCm');
    const diametroControl = this.form.get('diametroCm');

    // Enable/disable based on type
    if (tipo === TipoEmbalagemEnum.PacoteCaixa) {
      larguraControl?.enable();
      alturaControl?.enable();
      comprimentoControl?.enable();
      diametroControl?.disable();
    } else if (tipo === TipoEmbalagemEnum.RoloCilindro) {
      larguraControl?.disable();
      alturaControl?.disable();
      comprimentoControl?.enable();
      diametroControl?.enable();
    } else if (tipo === TipoEmbalagemEnum.Envelope) {
      larguraControl?.enable();
      alturaControl?.disable();
      comprimentoControl?.enable();
      diametroControl?.disable();
    }
  }

  get selectedTipo(): number {
    return this.form.get('idTipoEmbalagem')?.value;
  }

  salvar(): void {
    if (this.isSaving) return;

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.alertService.show('Atenção', 'Verifique os campos obrigatórios.', AlertType.warning);
      return;
    }

    this.isSaving = true;
    const request: EmbalagemRequest = this.form.getRawValue();

    if (this.isEditMode && this.idEmbalagem) {
      this.embalagemService.update(this.idEmbalagem, request).subscribe({
        next: res => this.handleSuccess(res),
        error: err => this.handleError(err),
      });
    } else {
      this.embalagemService.create(request).subscribe({
        next: res => this.handleSuccess(res),
        error: err => this.handleError(err),
      });
    }
  }

  private handleSuccess(res: ReturnStruct): void {
    this.isSaving = false;
    if (res.isError) {
      this.alertService.show('Erro', res.errorDescription, AlertType.error);
      return;
    }
    this.alertService.show('Sucesso', 'Embalagem salva com sucesso.', AlertType.success);
    this.router.navigate(['/cadastros/embalagens']);
  }

  private handleError(err: any): void {
    this.isSaving = false;
    this.alertService.show('Erro', 'Ocorreu um erro ao salvar.', AlertType.error);
  }

  cancelar(): void {
    this.router.navigate(['/cadastros/embalagens']);
  }
}
